/* eslint-disable */
/**
 * CEOCommandCenter — Moderne dashboard med glassmorphism, animerede KPI'er og live charts
 * Erstatter den grundlæggende DashboardVisning med en visuelt fremtidsrettet kontrolpanel.
 *
 * Importér i App.js: import CEOCommandCenter from './CEOCommandCenter';
 * Brug i stedet for / ovenpå DashboardVisning.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useSpring, useTransform } from 'framer-motion';
import {
 AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
 BarChart, Bar, Cell, PieChart, Pie, RadialBarChart, RadialBar,
} from 'recharts';
import { api } from './api';
import { T, ThemeContext } from './tema';
import { SvIkon } from './SvIkon';
// ── Responsivt CSS — injiceres én gang ──
if (typeof document !== 'undefined' && !document.getElementById('sv-ceo-css')) {
 const s = document.createElement('style');
 s.id = 'sv-ceo-css';
 s.textContent = `
 @media (max-width: 767px) {
 /* Grids: stack til 1 kolonne */
 .sv-ceo-akti-grid { grid-template-columns: 1fr !important; gap: 12px !important; }
 .sv-ceo-stat-grid { grid-template-columns: 1fr !important; gap: 12px !important; }
 /* KPI: 2 kolonner på mobil */
 .sv-ceo-kpi-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; }
 /* Dashboard padding reduceret */
 .sv-ceo-wrap { padding: 14px !important; }
 /* Header-linje kompakt */
 .sv-ceo-header { margin-bottom: 16px !important; }
 .sv-ceo-header-titel { font-size: 16px !important; }
 .sv-ceo-header-tid { display: none !important; }
 /* Diagrammer lidt kortere */
 .sv-ceo-chart-box { height: 160px !important; }
 }
 @media (min-width: 640px) and (max-width: 1023px) {
 /* Tablet: 2 kolonner til stat-grid */
 .sv-ceo-stat-grid { grid-template-columns: repeat(2, 1fr) !important; }
 .sv-ceo-akti-grid { grid-template-columns: 1fr !important; }
 }
 `;
 document.head.appendChild(s);
}
// ── Animeret tal-counter ───────────────────────────────────
function AnimatedNumber({ value, duration = 1.2, suffix = '' }) {
 const spring = useSpring(0, { duration: duration * 1000, stiffness: 80, damping: 20 });
 const display = useTransform(spring, v => Math.round(v).toLocaleString('da-DK') + suffix);
 useEffect(() => { spring.set(value); }, [value, spring]);
 return <motion.span>{display}</motion.span>;
}
// ── Sparkline-mini-chart ──────────────────────────────────
function Sparkline({ data = [], farve = '#3fd4c7', højde = 40 }) {
 if (!data.length) return null;
 const max = Math.max(...data, 1);
 const min = Math.min(...data);
 const range = max - min || 1;
 const w = 80;
 const h = højde;
 const pts = data.map((v, i) => {
 const x = (i / (data.length - 1)) * w;
 const y = h - ((v - min) / range) * (h - 6) - 3;
 return `${x},${y}`;
 });
 const path = `M${pts.join(' L')}`;
 const area = `M${pts[0]} L${pts.join(' L')} L${w},${h} L0,${h} Z`;
 return (
 <svg width={w} height={h} style={{ overflow: 'visible', opacity: 0.8 }}> <defs> <linearGradient id={`spark_${farve.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1"> <stop offset="0%" stopColor={farve} stopOpacity={0.3} /> <stop offset="100%" stopColor={farve} stopOpacity={0} /> </linearGradient> </defs> <path d={area} fill={`url(#spark_${farve.replace('#', '')})`} /> <path d={path} stroke={farve} strokeWidth={1.5} fill="none" /> <circle cx={pts[pts.length - 1].split(',')[0]} cy={pts[pts.length - 1].split(',')[1]}
 r={2.5} fill={farve} /> </svg>
 );
}
// ── KPI Glas-kort ─────────────────────────────────────────
function KPIKort({ ikon, label, tal, sub, farve, sparkData = [], trend_pct, index = 0 }) {
 const [hover, setHover] = useState(false);
 const { theme_mode } = React.useContext(ThemeContext);
 const lys = theme_mode === 'light';
 const pos = trend_pct > 0 ? '↑' : trend_pct < 0 ? '↓' : null;
 const trendFarve = trend_pct > 0 ? '#4ade80' : trend_pct < 0 ? '#f87171' : '#94a3b8';
 return (
 <motion.div
 initial={{ opacity: 0, y: 32 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: index * 0.08, type: 'spring', stiffness: 180, damping: 24 }}
 onHoverStart={() => setHover(true)}
 onHoverEnd={() => setHover(false)}
 style={{
 position: 'relative',
 background: hover
 ? `linear-gradient(135deg, ${farve}12, ${lys ? 'rgba(15,23,42,0.04)' : 'rgba(255,255,255,0.06)'})`
 : lys ? 'rgba(255,255,255,0.90)' : 'rgba(255,255,255,0.04)',
 border: `1px solid ${hover ? farve + '55' : lys ? 'rgba(15,23,42,0.10)' : 'rgba(255,255,255,0.09)'}`,
 borderTop: `3px solid ${farve}`,
 borderRadius: 18,
 padding: '22px 24px',
 backdropFilter: 'blur(20px)',
 WebkitBackdropFilter: 'blur(20px)',
 boxShadow: hover
 ? `0 8px 40px ${farve}22, 0 2px 12px ${lys ? 'rgba(15,23,42,0.10)' : 'rgba(0,0,0,0.3)'}, inset 0 1px 0 ${lys ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.08)'}`
 : lys ? '0 2px 12px rgba(15,23,42,0.07), 0 1px 0 rgba(255,255,255,0.9) inset' : '0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)',
 transform: hover ? 'translateY(-3px)' : 'none',
 transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
 cursor: 'default',
 overflow: 'hidden',
 }}
 >
 {/* Glød-baggrund på hover */}
 {hover && (
 <div style={{
 position: 'absolute', inset: 0,
 background: `radial-gradient(ellipse at top left, ${farve}10 0%, transparent 60%)`,
 pointerEvents: 'none',
 }} />
 )}
 {/* Header: ikon + label */}
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}> <div style={{
 width: 44, height: 44, borderRadius: 12,
 background: farve + '18',
 border: `1px solid ${farve}33`,
 display: 'flex', alignItems: 'center', justifyContent: 'center',
 fontSize: 22,
 boxShadow: hover ? `0 0 20px ${farve}33` : 'none',
 transition: 'box-shadow 0.3s',
 }}>
 {ikon}
 </div> <div style={{
 fontSize: 10, fontWeight: 700, color: T.subtil,
 letterSpacing: '0.12em', textTransform: 'uppercase',
 maxWidth: 120, textAlign: 'right', lineHeight: 1.4,
 }}>
 {label}
 </div> </div>
 {/* Tal */}
 <div className={hover ? 'sv-num-glow' : ''} style={{
 fontSize: 40, fontWeight: 800, color: farve,
 lineHeight: 1, letterSpacing: '-0.03em',
 marginBottom: 8,
 textShadow: hover ? `0 0 28px ${farve}66, 0 0 60px ${farve}22` : 'none',
 transition: 'text-shadow 0.3s',
 }}> <AnimatedNumber value={tal} /> </div>
 {/* Sub-tekst + trend */}
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}> <div style={{ fontSize: 12, color: T.dæmpet, lineHeight: 1.4 }}>{sub}</div>
 {trend_pct !== undefined && trend_pct !== null && (
 <div style={{
 fontSize: 11, fontWeight: 700, color: trendFarve,
 background: trendFarve + '18', border: `1px solid ${trendFarve}33`,
 borderRadius: 20, padding: '2px 8px',
 display: 'flex', alignItems: 'center', gap: 3,
 }}>
 {pos} {Math.abs(Math.round(trend_pct))}%
 </div>
 )}
 </div>
 {/* Sparkline */}
 {sparkData.length > 1 && (
 <div style={{ position: 'absolute', bottom: 16, right: 16, opacity: hover ? 1 : 0.6, transition: 'opacity 0.3s' }}> <Sparkline data={sparkData} farve={farve} /> </div>
 )}
 </motion.div>
 );
}
// ── Deadline-kort ─────────────────────────────────────────
function DeadlineKort({ d, index }) {
 const dage = d.dage_til;
 const farve = dage < 0 ? '#f87171' : dage <= 3 ? '#fb923c' : dage <= 7 ? '#fbbf24' : '#4ade80';
 const label = dage < 0 ? `${Math.abs(dage)}d overskredet` : dage === 0 ? 'I dag' : `${dage} dage`;
 const { theme_mode } = React.useContext(ThemeContext);
 const lys = theme_mode === 'light';
 return (
 <motion.div
 initial={{ opacity: 0, x: -20 }}
 animate={{ opacity: 1, x: 0 }}
 transition={{ delay: 0.05 + index * 0.04, type: 'spring', stiffness: 200, damping: 26 }}
 style={{
 display: 'flex', alignItems: 'center', gap: 12,
 padding: '10px 14px',
 background: lys ? 'rgba(255,255,255,0.80)' : 'rgba(255,255,255,0.03)',
 border: lys ? `1px solid rgba(15,23,42,0.07)` : `1px solid rgba(255,255,255,0.06)`,
 borderLeft: `3px solid ${farve}`,
 borderRadius: 10,
 transition: 'background 0.2s',
 }}
 > <div style={{
 minWidth: 60, textAlign: 'center',
 background: farve + '18', borderRadius: 8, padding: '3px 6px',
 fontSize: 11, fontWeight: 700, color: farve,
 }}>
 {label}
 </div> <div style={{ flex: 1, minWidth: 0 }}> <div style={{ fontSize: 13, fontWeight: 500, color: T.creme, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
 {d.klient_navn}
 </div> <div style={{ fontSize: 11, color: T.subtil }}>
 {d.sagsnummer} · {d.sagstype || 'Sag'}
 {d.initialer ? ` · ${d.initialer}` : ''}
 </div> </div> <div style={{ fontSize: 11, color: T.subtil, whiteSpace: 'nowrap' }}>
 {d.deadline ? new Date(d.deadline).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' }) : ''}
 </div> </motion.div>
 );
}
// ── Aktivitetsfeed-post ───────────────────────────────────
function AktivitetPost({ a, index }) {
 const TYPE_IKONER = {
 note: '', mail: '', mail_ind: '', mail_ud: '',
 opkald: '', moede: '', dokument: '', deadline: '',
 aktindsigt: '', aftale: '', myndighedsbrev: '',
 };
 const ikon = TYPE_IKONER[a.type] || '';
 const tidspunkt = a.dato
 ? new Date(a.dato).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })
 : '';
 return (
 <motion.div
 initial={{ opacity: 0, x: 16 }}
 animate={{ opacity: 1, x: 0 }}
 transition={{ delay: index * 0.03 }}
 style={{
 display: 'flex', alignItems: 'flex-start', gap: 10,
 padding: '8px 0',
 borderBottom: '1px solid rgba(255,255,255,0.04)',
 }}
 > <span style={{ fontSize: 16, lineHeight: 1.4, flexShrink: 0 }}>{ikon}</span> <div style={{ flex: 1, minWidth: 0 }}> <div style={{
 fontSize: 12, color: T.creme,
 whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
 }}>
 {a.titel || a.type}
 </div> <div style={{ fontSize: 11, color: T.subtil }}>
 {a.klient_navn || a.sagsnummer}
 {a.forfatter ? ` · ${a.forfatter}` : ''}
 </div> </div> <div style={{ fontSize: 10, color: T.dæmpet, whiteSpace: 'nowrap', marginTop: 2 }}>{tidspunkt}</div> </motion.div>
 );
}
// ── Tilpasset tooltip ─────────────────────────────────────
function DashTooltip({ active, payload, label }) {
 if (!active || !payload?.length) return null;
 return (
 <div style={{
 background: 'rgba(8,12,32,0.95)', border: '1px solid rgba(255,255,255,0.12)',
 borderRadius: 10, padding: '10px 14px', fontSize: 12,
 boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
 }}> <div style={{ color: T.dæmpet, marginBottom: 6, fontWeight: 600 }}>{label}</div>
 {payload.map(p => (
 <div key={p.name} style={{ color: p.color, marginBottom: 2 }}>
 {p.name}: <strong>{p.value}</strong> </div>
 ))}
 </div>
 );
}
// ── Sektion-header ─────────────────────────────────────────
function SektionHeader({ ikon, titel, antal }) {
 return (
 <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
 <SvIkon navn={ikon} size={16} color={T.dæmpet} />
 <div style={{ flex: 1, fontSize: 12, fontWeight: 700, color: T.dæmpet, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
 {titel}
 </div>
 {antal !== undefined && (
 <div style={{ fontSize: 11, color: T.subtil, background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: '2px 8px' }}>
 {antal}
 </div>
 )}
 </div>
 );
}
// ── Glas-panel ────────────────────────────────────────────
function GlasPanel({ children, style }) {
 const { theme_mode } = React.useContext(ThemeContext);
 const lys = theme_mode === 'light';
 return (
 <div className="sv-3d-tilt" style={{
 position: 'relative',
 overflow: 'hidden',
 background: lys
 ? 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.90) 100%)'
 : 'linear-gradient(135deg, rgba(10,14,46,0.85) 0%, rgba(15,20,52,0.80) 100%)',
 border: lys ? '1px solid rgba(15,23,42,0.08)' : '1px solid rgba(255,255,255,0.09)',
 borderRadius: 18,
 padding: '22px 24px',
 backdropFilter: 'blur(24px)',
 WebkitBackdropFilter: 'blur(24px)',
 boxShadow: lys
 ? '0 4px 24px rgba(15,23,42,0.08), inset 0 1px 0 rgba(255,255,255,1)'
 : '0 8px 40px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.07)',
 ...style,
 }}>
 {/* Accent top line */}
 <div style={{ position:'absolute', top:0, left:0, right:0, height:1, background:'linear-gradient(90deg,transparent,rgba(63,212,199,0.25),rgba(167,139,250,0.2),transparent)', pointerEvents:'none', zIndex:1 }} />
 {children}
 </div>
 );
}
// ─────────────────────────────────────────────────────────
// HOVED-KOMPONENT
// ─────────────────────────────────────────────────────────
export default function CEOCommandCenter({ onÅbnSag }) {
 const [kpi, setKpi] = useState(null);
 const [deadlines, setDeadlines] = useState([]);
 const [aktivitet, setAktivitet] = useState([]);
 const [risiko, setRisiko] = useState(null);
 const [seneste, setSeneste] = useState([]);
 const [sagstyper, setSagstyper] = useState([]);
 const [trend, setTrend] = useState(null);
 const [loader, setLoader] = useState(true);
 const [fejl, setFejl] = useState(null);
 const [sidst_opdateret, setSidstOpdateret] = useState(null);
 const hent = useCallback(() => {
 setFejl(null);
 Promise.all([
 api('dashboard/kpi').catch(() => null),
 api('dashboard/deadlines?dage=14').catch(() => null),
 api('dashboard/aktivitet?dage=30').catch(() => null),
 api('dashboard/risiko').catch(() => null),
 api('dashboard/seneste-aktivitet').catch(() => null),
 api('dashboard/sagstyper').catch(() => null),
 api('dashboard/trend').catch(() => null),
 ]).then(([k, d, a, r, sa, st, t]) => {
 if (k && !k.fejl) setKpi(k);
 if (d?.deadlines) setDeadlines(d.deadlines);
 if (a?.aktivitet) setAktivitet(a.aktivitet);
 if (r && !r.fejl) setRisiko(r);
 if (sa?.aktivitet) setSeneste(sa.aktivitet.slice(0, 12));
 if (st?.sagstyper) setSagstyper(st.sagstyper.slice(0, 8));
 if (t && !t.fejl) setTrend(t);
 setSidstOpdateret(new Date());
 setLoader(false);
 }).catch(() => {
 setFejl('Kunne ikke hente dashboard-data');
 setLoader(false);
 });
 }, []);
 useEffect(() => {
 hent();
 const interval = setInterval(hent, 90000); // refresh hvert 90s
 return () => clearInterval(interval);
 }, [hent]);
 // ── Aktivitets-sparklines fra historik ──
 const spark_data = aktivitet.slice(-14).map(a => parseInt(a.antal) || 0);
 // ── Sagstype-farver ──
 const TYPE_FARVER = ['#3fd4c7','#60a5fa','#a78bfa','#f59e0b','#f87171','#34d399','#fb923c','#e879f9'];
 if (loader) return (
 <div style={{ padding: 60, textAlign: 'center' }}> <motion.div
 animate={{ rotate: 360 }}
 transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
 style={{
 width: 40, height: 40, margin: '0 auto 20px',
 border: '3px solid rgba(63,212,199,0.2)',
 borderTopColor: T.turkis,
 borderRadius: '50%',
 }}
 /> <div style={{ color: T.dæmpet, fontSize: 13 }}>Henter data…</div> </div>
 );
 if (fejl) return (
 <div style={{ padding: 60, textAlign: 'center' }}> <div style={{ fontSize: 40, marginBottom: 16 }}></div> <div style={{ color: T.creme, marginBottom: 16 }}>{fejl}</div> <button onClick={hent} style={{
 background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', border: 'none',
 borderRadius: 10, padding: '10px 24px', color: '#fff',
 fontSize: 13, fontWeight: 600, cursor: 'pointer',
 fontFamily: "'Inter',system-ui,sans-serif",
 }}> Prøv igen</button> </div>
 );
 // ── KPI-definitioner ──
 const kpi_data = kpi ? [
 {
 ikon: '', label: 'Aktive sager', tal: kpi.aktive_sager, farve: '#3fd4c7',
 sub: `${kpi.sager_sidste_30dage} nye de seneste 30 dage`,
 trend_pct: trend?.nye_sager_ændring_pct, sparkData: spark_data,
 },
 {
 ikon: '', label: 'Deadlines — 7 dage', tal: kpi.deadlines_7dage, farve: '#fbbf24',
 sub: kpi.deadlines_overskredet > 0 ? `${kpi.deadlines_overskredet} overskredet!` : 'Alle under kontrol',
 trend_pct: null,
 },
 {
 ikon: '', label: 'Høj risiko', tal: kpi.hoej_risiko, farve: '#f87171',
 sub: `Gns. risiko: ${Math.round(kpi.gns_risiko || 0)} point`,
 trend_pct: null,
 },
 {
 ikon: 'Zap', label: 'Aktivitet i dag', tal: trend?.aktivitet_i_dag ?? 0, farve: '#60a5fa',
 sub: `I går: ${trend?.aktivitet_i_gaar ?? 0} hændelser`,
 trend_pct: trend?.aktivitet_ændring_pct,
 },
 {
 ikon: '', label: 'Klienter i alt', tal: kpi.total_klienter, farve: '#a78bfa',
 sub: `${kpi.aktive_sager} aktive sager`,
 trend_pct: null,
 },
 ] : [];
 const risiko_data = risiko ? [
 { name: 'Lav', value: risiko.lav || 0, fill: '#4ade80' },
 { name: 'Middel', value: risiko.middel || 0, fill: '#fbbf24' },
 { name: 'Høj', value: risiko.hoej || 0, fill: '#f87171' },
 ] : [];
 const aktivitet_data = aktivitet.map(a => ({
 dato: new Date(a.dato).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' }),
 Hændelser: parseInt(a.antal) || 0,
 Dokumenter: parseInt(a.dokumenter) || 0,
 }));
 const deadline_overskredet = deadlines.filter(d => d.dage_til < 0);
 const deadline_kommende = deadlines.filter(d => d.dage_til >= 0).slice(0, 8);
 return (
 <div className="sv-ceo-wrap" style={{ padding: 'clamp(16px,3vw,32px)', maxWidth: 1440, margin: '0 auto' }}>
 {/* ── Header-linje ── */}
 <div className="sv-ceo-header" style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}> <div> <div className="sv-gradient-text sv-ceo-header-titel" style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>
 Kontrolpanel
 </div>
 {sidst_opdateret && (
 <div className="sv-ceo-header-tid" style={{ fontSize: 11, color: T.subtil, marginTop: 2 }}>
 Opdateret {sidst_opdateret.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })}
 </div>
 )}
 </div> <div style={{ flex: 1 }} /> <motion.button
 whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
 onClick={hent}
 style={{
 background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
 borderRadius: 10, padding: '8px 16px', color: T.dæmpet,
 fontSize: 12, cursor: 'pointer', fontFamily: "'Inter',system-ui,sans-serif",
 display: 'flex', alignItems: 'center', gap: 6,
 }}
 >
 Opdater
 </motion.button> </div>
 {/* ── KPI GRID ── */}
 <div className="sv-ceo-kpi-grid" style={{
 display: 'grid',
 gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
 gap: 14, marginBottom: 24,
 }}>
 {kpi_data.map((k, i) => (
 <KPIKort key={k.label} index={i} {...k} />
 ))}
 </div>
 {/* ── Aktivitets-chart + Deadlines ── */}
 <div className="sv-ceo-akti-grid" style={{
 display: 'grid',
 gridTemplateColumns: 'minmax(0,1.6fr) minmax(0,1fr)',
 gap: 16, marginBottom: 16,
 }}>
 {/* Aktivitetsgraf */}
 <GlasPanel> <SektionHeader ikon="TrendingUp" titel="Aktivitet — seneste 30 dage" />
 {aktivitet_data.length > 0 ? (
 <ResponsiveContainer className="sv-ceo-chart-box" width="100%" height={200}> <AreaChart data={aktivitet_data} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}> <defs> <linearGradient id="grad_haend" x1="0" y1="0" x2="0" y2="1"> <stop offset="0%" stopColor="#3fd4c7" stopOpacity={0.35} /> <stop offset="100%" stopColor="#3fd4c7" stopOpacity={0} /> </linearGradient> <linearGradient id="grad_dok" x1="0" y1="0" x2="0" y2="1"> <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.25} /> <stop offset="100%" stopColor="#60a5fa" stopOpacity={0} /> </linearGradient> </defs> <XAxis dataKey="dato" tick={{ fontSize: 10, fill: '#475569' }}
 axisLine={false} tickLine={false}
 interval={Math.ceil(aktivitet_data.length / 8)} /> <YAxis tick={{ fontSize: 10, fill: '#475569' }} axisLine={false} tickLine={false} /> <Tooltip content={<DashTooltip />} /> <Area type="monotone" dataKey="Hændelser" stroke="#3fd4c7" strokeWidth={2.5}
 fill="url(#grad_haend)" dot={false} activeDot={{ r: 4, fill: '#3fd4c7' }} /> <Area type="monotone" dataKey="Dokumenter" stroke="#60a5fa" strokeWidth={1.5}
 fill="url(#grad_dok)" dot={false} activeDot={{ r: 3, fill: '#60a5fa' }} /> </AreaChart> </ResponsiveContainer>
 ) : (
 <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.subtil, fontSize: 13 }}>
 Ingen aktivitetsdata
 </div>
 )}
 </GlasPanel>
 {/* Deadlines */}
 <GlasPanel> <SektionHeader
 ikon="AlarmClock"
 titel="Kommende deadlines"
 antal={deadlines.length}
 /> <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 230, overflowY: 'auto' }}>
 {deadline_overskredet.length > 0 && (
 <div style={{ fontSize: 10, fontWeight: 700, color: '#f87171', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 2 }}>
 OVERSKREDET
 </div>
 )}
 {deadline_overskredet.map((d, i) => <DeadlineKort key={d.sag_id} d={d} index={i} />)}
 {deadline_kommende.length > 0 && deadline_overskredet.length > 0 && (
 <div style={{ fontSize: 10, fontWeight: 700, color: T.subtil, letterSpacing: '0.1em', textTransform: 'uppercase', margin: '8px 0 2px' }}>
 KOMMENDE
 </div>
 )}
 {deadline_kommende.map((d, i) => <DeadlineKort key={d.sag_id} d={d} index={i + deadline_overskredet.length} />)}
 {deadlines.length === 0 && (
 <div style={{ textAlign: 'center', padding: '32px 0', color: T.dæmpet, fontSize: 13 }}> <div style={{ fontSize: 28, marginBottom: 8 }}></div>
 Ingen deadlines de næste 14 dage
 </div>
 )}
 </div> </GlasPanel> </div>
 {/* ── Risiko + Sagstyper + Seneste aktivitet ── */}
 <div className="sv-ceo-stat-grid" style={{
 display: 'grid',
 gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)',
 gap: 16,
 }}>
 {/* Risikofordeling */}
 <GlasPanel> <SektionHeader ikon="Shield" titel="Risikofordeling" />
 {risiko ? (
 <> <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}> <PieChart width={140} height={140}> <Pie
 data={risiko_data}
 cx={70} cy={70}
 innerRadius={42} outerRadius={62}
 paddingAngle={3}
 dataKey="value"
 stroke="none"
 >
 {risiko_data.map((entry, index) => (
 <Cell key={index} fill={entry.fill} />
 ))}
 </Pie> <Tooltip
 contentStyle={{ background: 'rgba(8,12,32,0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 11 }}
 formatter={(v, n) => [`${v} sager`, n]}
 /> </PieChart> </div>
 {risiko_data.map(r => {
 const total = risiko_data.reduce((s, x) => s + x.value, 0) || 1;
 const pct = Math.round((r.value / total) * 100);
 return (
 <div key={r.name} style={{ marginBottom: 10 }}> <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}> <span style={{ color: T.dæmpet }}>{r.name}</span> <span style={{ color: r.fill, fontWeight: 600 }}>{r.value} ({pct}%)</span> </div> <motion.div
 style={{ height: 5, background: 'rgba(255,255,255,0.05)', borderRadius: 3, overflow: 'hidden' }}
 > <motion.div
 initial={{ width: 0 }}
 animate={{ width: `${pct}%` }}
 transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
 style={{ height: '100%', background: r.fill, borderRadius: 3 }}
 /> </motion.div> </div>
 );
 })}
 <div style={{ fontSize: 11, color: T.subtil, marginTop: 8, textAlign: 'center' }}>
 Gns. risiko: {Math.round(risiko.gns || 0)} point
 </div> </>
 ) : (
 <div style={{ textAlign: 'center', padding: '24px 0', color: T.subtil, fontSize: 12 }}>Ingen data</div>
 )}
 </GlasPanel>
 {/* Sagstyper */}
 <GlasPanel> <SektionHeader ikon="BarChart2" titel="Sagstyper" antal={sagstyper.length} />
 {sagstyper.length > 0 ? (
 <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
 {sagstyper.map((s, i) => {
 const max = Math.max(...sagstyper.map(x => parseInt(x.antal) || 0), 1);
 const pct = Math.round(((parseInt(s.antal) || 0) / max) * 100);
 const farve = TYPE_FARVER[i % TYPE_FARVER.length];
 return (
 <div key={s.sagstype || i}> <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}> <span style={{ color: T.dæmpet, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
 {s.sagstype || 'Ukendt'}
 </span> <span style={{ color: farve, fontWeight: 600 }}>{s.antal}</span> </div> <motion.div style={{ height: 5, background: 'rgba(255,255,255,0.05)', borderRadius: 3, overflow: 'hidden' }}> <motion.div
 initial={{ width: 0 }}
 animate={{ width: `${pct}%` }}
 transition={{ duration: 0.7, delay: 0.2 + i * 0.05, ease: 'easeOut' }}
 style={{ height: '100%', background: farve, borderRadius: 3 }}
 /> </motion.div> </div>
 );
 })}
 </div>
 ) : (
 <div style={{ textAlign: 'center', padding: '24px 0', color: T.subtil, fontSize: 12 }}>Ingen sagstyper</div>
 )}
 </GlasPanel>
 {/* Seneste aktivitet */}
 <GlasPanel> <SektionHeader ikon="Zap" titel="Seneste aktivitet" antal={seneste.length} />
 {seneste.length > 0 ? (
 <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 280, overflowY: 'auto' }}>
 {seneste.map((a, i) => <AktivitetPost key={a.id || i} a={a} index={i} />)}
 </div>
 ) : (
 <div style={{ textAlign: 'center', padding: '32px 0', color: T.subtil, fontSize: 13 }}> <div style={{ fontSize: 28, marginBottom: 8 }}></div>
 Ingen aktivitet endnu i dag
 </div>
 )}
 </GlasPanel> </div> </div>
 );
}